import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  Grid,
  Group,
  SegmentedControl,
  Stack,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDeviceFloppy, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { api } from "../api/client";
import type { LabelElement, LabelTemplate, LabelType } from "../api/types";
import { BackgroundLibrary } from "../components/labelEditor/BackgroundLibrary";
import { BackgroundPicker, loadStoredBackground } from "../components/labelEditor/BackgroundPicker";
import { Canvas } from "../components/labelEditor/Canvas";
import { ElementInspector } from "../components/labelEditor/ElementInspector";
import { ElementList } from "../components/labelEditor/ElementList";

const LABEL_TYPES: LabelType[] = [
  "device", "box", "partida_small", "partida_large", "link_small", "link_large",
  "passport_small", "passport_large", "stock_item",
];

export function LabelEditor() {
  const { t } = useTranslation();
  const [type, setType] = useState<LabelType>("device");
  const [templates, setTemplates] = useState<Record<LabelType, LabelTemplate> | null>(null);
  const [draft, setDraft] = useState<Record<LabelType, LabelTemplate> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewBg, setPreviewBg] = useState<string>("default");

  async function load() {
    try {
      const data = await api.listLabelTemplates();
      setTemplates(data);
      setDraft(JSON.parse(JSON.stringify(data)));
    } catch {
      notifications.show({ color: "red", message: t("labelEditor.loadFailed") });
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { setPreviewBg(loadStoredBackground(type)); }, [type]);

  const tmpl = draft?.[type];
  const dirty = useMemo(
    () => JSON.stringify(templates?.[type]) !== JSON.stringify(draft?.[type]),
    [templates, draft, type],
  );
  const selectedElement = tmpl?.elements.find((e) => e.id === selectedId) ?? null;
  const resolvedPreviewBgId = (() => {
    if (!tmpl) return null;
    if (previewBg === "none") return null;
    if (previewBg === "default") return tmpl.default_background_id;
    return previewBg;
  })();

  function patchElement(id: string, patch: Partial<LabelElement>) {
    if (!draft) return;
    setDraft({
      ...draft,
      [type]: {
        ...draft[type],
        elements: draft[type].elements.map((e) =>
          e.id === id ? ({ ...e, ...patch } as LabelElement) : e),
      },
    });
  }

  function addElement(el: LabelElement) {
    if (!draft) return;
    setDraft({
      ...draft,
      [type]: { ...draft[type], elements: [...draft[type].elements, el] },
    });
    setSelectedId(el.id);
  }

  function deleteElement(id: string) {
    if (!draft) return;
    setDraft({
      ...draft,
      [type]: { ...draft[type], elements: draft[type].elements.filter((e) => e.id !== id) },
    });
    if (selectedId === id) setSelectedId(null);
  }

  function move(id: string, dir: -1 | 1) {
    if (!draft) return;
    const arr = [...draft[type].elements];
    const idx = arr.findIndex((e) => e.id === id);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setDraft({ ...draft, [type]: { ...draft[type], elements: arr } });
  }

  async function save() {
    if (!draft) return;
    try {
      const saved = await api.updateLabelTemplate(type, {
        elements: draft[type].elements,
        default_background_id: draft[type].default_background_id,
      });
      setTemplates((prev) => prev ? { ...prev, [type]: saved } : prev);
      setDraft((prev) => prev ? { ...prev, [type]: saved } : prev);
      notifications.show({ color: "green", message: t("labelEditor.saved") });
    } catch {
      notifications.show({ color: "red", message: t("labelEditor.saveFailed") });
    }
  }

  async function resetType() {
    if (!window.confirm(t("labelEditor.resetConfirm"))) return;
    try {
      await api.resetLabelTemplate(type);
      await load();
      notifications.show({ color: "green", message: t("labelEditor.resetDone") });
    } catch {
      notifications.show({ color: "red", message: t("labelEditor.resetFailed") });
    }
  }

  if (!draft || !tmpl) return null;

  return (
    <Container size="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>{t("labelEditor.title")}</Title>
        <Group>
          <Button variant="subtle" leftSection={<IconRefresh size={14} />} onClick={resetType}>
            {t("labelEditor.reset")}
          </Button>
          <Button leftSection={<IconDeviceFloppy size={14} />} onClick={save} disabled={!dirty}>
            {t("labelEditor.save")}
          </Button>
        </Group>
      </Group>

      <SegmentedControl
        mb="md"
        value={type}
        onChange={(v) => { setType(v as LabelType); setSelectedId(null); }}
        data={LABEL_TYPES.map((lt) => ({ value: lt, label: t(`labelEditor.type.${lt}`) }))}
      />

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack>
            <Group justify="space-between">
              <BackgroundPicker labelType={type} value={previewBg} onChange={setPreviewBg} />
            </Group>
            <Canvas
              labelType={type}
              template={tmpl}
              selectedId={selectedId}
              previewBackgroundId={resolvedPreviewBgId}
              onSelect={setSelectedId}
              onMove={(id, x, y) => patchElement(id, { x, y })}
            />
            <BackgroundLibrary
              labelType={type}
              backgrounds={tmpl.backgrounds}
              defaultBackgroundId={tmpl.default_background_id}
              onRefresh={load}
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <ElementList
              labelType={type}
              elements={tmpl.elements}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onAdd={addElement}
              onDelete={deleteElement}
              onMoveUp={(id) => move(id, -1)}
              onMoveDown={(id) => move(id, 1)}
            />
            <ElementInspector
              labelType={type}
              element={selectedElement}
              onChange={(patch) => selectedId && patchElement(selectedId, patch)}
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
